import React, {useEffect} from 'react';
import {HorizontalCardProps} from './types';
import styled from 'styled-components';
import {BOXSHADOWS, COLORS, focusStyles} from '../../styles';
import {HorizontalCardThumbnail} from './HorizontalCardThumbnail';
import {HorizontalCardBody} from './HorizontalCardBody';
import {HorizontalCardActions} from './HorizontalCardActions';
import {defaultOnMouseDownHandler, useActionWithin} from '../../common';

export const HorizontalCardContentContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  background-color: ${COLORS.white};
  border-radius: 8px;
`;

export const HorizontalCardContainer = styled.div`
  display: flex;
  flex: 1;

  &.outline {
    ${HorizontalCardContentContainer} {
      box-shadow: inset 0 0 0 1px ${COLORS.neutral_200};
    }
  }

  &.elevated {
    ${HorizontalCardContentContainer} {
      box-shadow: ${BOXSHADOWS.BOXSHADOW_L1};
    }
  }

  &.clickable {
    cursor: pointer;

    &:hover:not(.action-within):not(.disabled) {
      &.outline {
        ${HorizontalCardContentContainer} {
          background-color: ${COLORS.primary_20};
        }
      }

      &.elevated {
        ${HorizontalCardContentContainer} {
          box-shadow: ${BOXSHADOWS.BOXSHADOW_L3};
        }
      }
    }

    &:active:not(.action-within):not(.disabled) {
      &.outline {
        ${HorizontalCardContentContainer} {
          background-color: ${COLORS.primary_100};
        }
      }

      &.elevated {
        ${HorizontalCardContentContainer} {
          box-shadow: ${BOXSHADOWS.BOXSHADOW_L2};
        }
      }
    }

    &:focus {
      ${focusStyles}
    }
  }

  &.disabled {
    cursor: not-allowed;

    &.elevated {
      ${HorizontalCardContentContainer} {
        box-shadow: ${BOXSHADOWS.BOXSHADOW_L1};
      }
    }

    &.outline {
      ${HorizontalCardContentContainer} {
        box-shadow: inset 0 0 0 1px ${COLORS.neutral_100};
      }
    }
  }
`;

export const HorizontalCard: React.FunctionComponent<HorizontalCardProps> = ({
                                                                               title,
                                                                               description,
                                                                               tags,
                                                                               progress,
                                                                               icon,
                                                                               variant = 'outline',
                                                                               image,
                                                                               action,
                                                                               actions,
                                                                               disabled,
                                                                               className,
                                                                               dataTestId,
                                                                               ...rest
                                                                             }) => {

  const [actionsRefs, setActionsRefs] = React.useState<React.RefObject<HTMLButtonElement>[]>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useActionWithin(containerRef, actionsRefs);

  const cls = `${variant} ${action ? 'clickable' : ''} ${disabled ? 'disabled' : ''} ${className || ''}`;

  const handleClick = () => {
    !disabled && action && action();
  }

  const handleButtonPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.key === 'Enter' && handleClick();
  };

  return (
    <HorizontalCardContainer ref={containerRef}
               tabIndex={action && !disabled ? 0 : -1}
               className={cls}
               onKeyDown={handleButtonPress}
               onClick={handleClick}
               onMouseDown={defaultOnMouseDownHandler}>
      <HorizontalCardContentContainer
               onMouseDown={defaultOnMouseDownHandler}
               data-testid={dataTestId}
               {...rest}>

        <HorizontalCardThumbnail image={image}
                                 icon={icon}/>

        <HorizontalCardBody title={title}
                            description={description}
                            tags={tags}
                            progress={progress}/>

        <HorizontalCardActions ref={instance => setActionsRefs(instance ?? [])}
                               actions={actions}
                               disabled={disabled}/>

      </HorizontalCardContentContainer>
    </HorizontalCardContainer>
  )
};

export default HorizontalCard;
